import { CommonModule } from '@angular/common';
import { Component, computed, effect, input, model, output, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { SelectButtonModule } from 'primeng/selectbutton';

import { DeleteMode } from '@access-management/access-management';

type EntityKind = 'user' | 'household';

@Component({
  selector: 'app-delete-entity-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, DialogModule, ButtonModule, InputTextModule, MessageModule, SelectButtonModule],
  templateUrl: './delete-entity-dialog.html',
  styleUrl: './delete-entity-dialog.scss'
})
export class DeleteEntityDialog {
  readonly visible = model<boolean>(false);
  readonly entityKind = input.required<EntityKind>();
  readonly entityName = input<string>('');
  readonly submitting = input<boolean>(false);

  readonly confirm = output<DeleteMode>();
  readonly cancel = output<void>();

  readonly selectedMode = signal<DeleteMode>('Soft');
  readonly confirmText = signal<string>('');

  readonly modeOptions: { label: string; value: DeleteMode }[] = [
    { label: 'Soft delete', value: 'Soft' },
    { label: 'Hard delete', value: 'Hard' }
  ];

  readonly isHard = computed<boolean>(() => this.selectedMode() === 'Hard');

  readonly canConfirm = computed<boolean>(() => !this.isHard() || this.confirmText().trim() === 'DELETE');

  readonly header = computed<string>(() =>
    this.entityKind() === 'household' ? 'Delete household' : 'Delete user'
  );

  readonly confirmLabel = computed<string>(() => (this.isHard() ? 'Delete permanently' : 'Delete'));

  readonly warning = computed<string>(() => {
    const household = this.entityKind() === 'household';
    if (this.isHard()) {
      return household
        ? 'Permanently deletes the household and every member, with all their data across AME and UsersEngine. Cannot be undone.'
        : 'Permanently deletes the user and all their data across AME and UsersEngine. Frees the email and external ID for reuse. Cannot be undone.';
    }
    return household
      ? 'Archives the household group. Members and their data are left as they are.'
      : 'Archives the user. Recoverable, but the email and external ID stay reserved.';
  });

  constructor() {
    effect(() => {
      if (this.visible()) {
        untracked(() => {
          this.selectedMode.set('Soft');
          this.confirmText.set('');
        });
      }
    });
  }

  onConfirm(): void {
    if (!this.canConfirm()) return;
    this.confirm.emit(this.selectedMode());
  }

  onCancel(): void {
    this.cancel.emit();
    this.visible.set(false);
  }

  onVisibleChange(value: boolean): void {
    if (!value) {
      this.cancel.emit();
    }
    this.visible.set(value);
  }
}
